// ink-component
import React, { RefObject, TextareaHTMLAttributes } from 'react';
import { ThemeConsumer } from 'styled-components';

import Counter from './components/NewTextAreaCounter/NewTextAreaCounter';
import CoreTextArea from '../_common/CoreTextArea/CoreTextArea';
import TableTextArea from './components/TableTextArea/TableTextArea';

import { ControllerProps } from '../_common/Controller/Controller';

export type NewTextAreaProps = Omit<ControllerProps, 'render' | 'theme'> &
  TextareaHTMLAttributes<HTMLTextAreaElement> & {
    /** Width of the TextArea */
    width?: 'large' | 'full';
    /** Height of the TextArea, in number of lines [(textarea row attribute docs)](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea#attr-rows) */
    rows?: number;
    /** Disables the resize functionality */
    unresizable?: boolean;
    /** Modifier for invalid state */
    invalid?: boolean;
    /** React ref object to be forwarded to the internal node */
    externalRef?: RefObject<HTMLTextAreaElement>;
    /** Allows screen readers to describe the content. The correct use of Field excludes the need for aria-label */
    'aria-label'?: string;
    /** Marks the element with a given ID to make it easier to select in tests */
    'data-testid'?: string | number;
    /** Unique name for analytics purposes. By default, components are not tracked, unless this is set */
    'data-track-name'?: string;
    /** Custom payload to be sent to analytics provider */
    'data-track-payload'?: any;
    /** List of tracked events in the analytics integration */
    'data-track-events'?: string[];
  };

/**
 * @purpose Allows users to insert long strings of data
 * @group NewTextArea
 * @categories form
 * @aka textarea
 * @HTMLType HTMLTextAreaElement
 */
function NewTextArea(props: NewTextAreaProps) {
  const { className, style, ...otherProps } = props;
  return (
    <ThemeConsumer>
      {cellTheme => {
        if (cellTheme && cellTheme.cellPreset === 'editable') {
          return <TableTextArea {...otherProps} />;
        }
        return <CoreTextArea {...otherProps} />;
      }}
    </ThemeConsumer>
  );
}

NewTextArea.Counter = Counter;

NewTextArea.defaultProps = {
  width: 'large',
  rows: 4,
  unresizable: false,
  invalid: false,
  'data-track-events': ['blur'],
};

NewTextArea.displayName = 'NewTextArea';

export { NewTextAreaCounterProps } from './components/NewTextAreaCounter/NewTextAreaCounter';

export default NewTextArea;
